/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Admin Backoffice Route Registry & Path-to-View Resolvers (Task 1.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import type { AdminViewId } from '../components/layout/AdminSidebar';

export interface AdminRouteConfig {
  id: AdminViewId;
  path: string;
  aliases?: string[];
  labelKey: string;
  requiresAuth: boolean;
}

/**
 * Single source of truth for all 21 Backoffice Module URL Routes.
 */
export const ADMIN_ROUTES: AdminRouteConfig[] = [
  {
    id: 'dashboard',
    path: '/dashboard',
    aliases: ['/', '', '/overview'],
    labelKey: 'admin.nav.dashboard',
    requiresAuth: true,
  },
  {
    id: 'aiModels',
    path: '/ai-models',
    aliases: ['/models', '/ai/models'],
    labelKey: 'admin.nav.aiModels',
    requiresAuth: true,
  },
  {
    id: 'aiSandbox',
    path: '/ai-sandbox',
    aliases: ['/sandbox', '/ai/sandbox'],
    labelKey: 'admin.nav.aiSandbox',
    requiresAuth: true,
  },
  {
    id: 'keypool',
    path: '/keypool',
    aliases: ['/keys', '/api-keys'],
    labelKey: 'admin.nav.keypool',
    requiresAuth: true,
  },
  {
    id: 'plans',
    path: '/plans',
    aliases: ['/pricing-plans'],
    labelKey: 'admin.nav.plans',
    requiresAuth: true,
  },
  {
    id: 'gateways',
    path: '/gateways',
    aliases: ['/payment-gateways'],
    labelKey: 'admin.nav.gateways',
    requiresAuth: true,
  },
  {
    id: 'orders',
    path: '/orders',
    aliases: ['/bank-transfers'],
    labelKey: 'admin.nav.orders',
    requiresAuth: true,
  },
  {
    id: 'orderLogs',
    path: '/orders/logs',
    aliases: ['/order-logs'],
    labelKey: 'admin.nav.orderLogs',
    requiresAuth: true,
  },
  {
    id: 'financial',
    path: '/financial',
    aliases: ['/finance'],
    labelKey: 'admin.nav.financial',
    requiresAuth: true,
  },
  {
    id: 'credits',
    path: '/credits',
    aliases: ['/credit-distribution'],
    labelKey: 'admin.nav.credits',
    requiresAuth: true,
  },
  {
    id: 'users',
    path: '/users',
    aliases: ['/user-directory'],
    labelKey: 'admin.nav.users',
    requiresAuth: true,
  },
  {
    id: 'cmsBlog',
    path: '/cms/blog',
    aliases: ['/cms-blog'],
    labelKey: 'admin.nav.cmsBlog',
    requiresAuth: true,
  },
  {
    id: 'cmsPages',
    path: '/cms/pages',
    aliases: ['/cms-pages'],
    labelKey: 'admin.nav.cmsPages',
    requiresAuth: true,
  },
  {
    id: 'cmsNavigation',
    path: '/cms/navigation',
    aliases: ['/navigation'],
    labelKey: 'admin.nav.cmsNavigation',
    requiresAuth: true,
  },
  {
    id: 'settings',
    path: '/settings',
    aliases: ['/system-settings'],
    labelKey: 'admin.nav.settings',
    requiresAuth: true,
  },
  {
    id: 'themeStudio',
    path: '/theme-studio',
    aliases: ['/themes'],
    labelKey: 'admin.nav.themeStudio',
    requiresAuth: true,
  },
  {
    id: 'templateStudio',
    path: '/template-studio',
    aliases: ['/templates'],
    labelKey: 'admin.nav.templateStudio',
    requiresAuth: true,
  },
  {
    id: 'brandStudio',
    path: '/brand-studio',
    aliases: ['/branding'],
    labelKey: 'admin.nav.brandStudio',
    requiresAuth: true,
  },
  {
    id: 'seedAssets',
    path: '/seed-assets',
    aliases: ['/assets-seeder'],
    labelKey: 'admin.nav.seedAssets',
    requiresAuth: true,
  },
  {
    id: 'tokenAnalytics',
    path: '/token-analytics',
    aliases: ['/tokens'],
    labelKey: 'admin.nav.tokenAnalytics',
    requiresAuth: true,
  },
  {
    id: 'auditLogs',
    path: '/audit-logs',
    aliases: ['/audit'],
    labelKey: 'admin.nav.auditLogs',
    requiresAuth: true,
  },
];

/**
 * Normalizes an arbitrary pathname by removing query string, trailing slashes,
 * and standardizing lowercase representation.
 */
export function normalizeAdminPath(rawPath: string): string {
  if (!rawPath) return '/dashboard';
  // Strip query strings and hash if passed safely with noUncheckedIndexedAccess
  const withoutQuery = rawPath.split('?')[0] ?? '';
  const withoutHash = withoutQuery.split('#')[0] ?? '';
  const clean = withoutHash.trim().toLowerCase();
  if (clean === '' || clean === '/') return '/dashboard';
  // Remove trailing slashes
  return clean.replace(/\/+$/, '') || '/dashboard';
}

/**
 * Resolves a given URL pathname into its corresponding AdminViewId.
 * Fallbacks safely to 'dashboard' if no matching route is found.
 */
export function resolvePathToView(pathname: string): AdminViewId {
  const normalized = normalizeAdminPath(pathname);

  // 1. Direct path match
  const directMatch = ADMIN_ROUTES.find((r) => r.path.toLowerCase() === normalized);
  if (directMatch) return directMatch.id;

  // 2. Alias match
  const aliasMatch = ADMIN_ROUTES.find((r) =>
    r.aliases?.some((a) => a.toLowerCase() === normalized || a.toLowerCase() === pathname.toLowerCase())
  );
  if (aliasMatch) return aliasMatch.id;

  return 'dashboard';
}

/**
 * Resolves an AdminViewId back to its canonical primary URL path.
 */
export function resolveViewToPath(viewId: AdminViewId): string {
  const route = ADMIN_ROUTES.find((r) => r.id === viewId);
  return route ? route.path : '/dashboard';
}

/**
 * Checks whether an incoming pathname maps to an existing module.
 */
export function isValidAdminPath(pathname: string): boolean {
  const normalized = normalizeAdminPath(pathname);
  return ADMIN_ROUTES.some(
    (r) =>
      r.path.toLowerCase() === normalized ||
      r.aliases?.some((a) => a.toLowerCase() === normalized || a.toLowerCase() === pathname.toLowerCase())
  );
}
